import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material'
import { useContext } from 'react'
import { useSearchParams } from 'react-router-dom'

import { QUERY_PARAMS } from '../../../constants'
import { AppContext } from '../../../context'
import { ActionType } from '../../../context/AppContextReducer'
import { anchorOrigin, transformOrigin } from '../../../theme/styleConstants'
import { PaymentMethod } from '../../../../generated'
import { getProperPaymentMethod } from '../../../utils'

/**
 * Payment methods in the order an accountant scans for them on a bank statement, rather than enum order.
 * Labels come from getProperPaymentMethod so they read exactly like the listing's Payment Method column
 * ("Check", not "Cheque").
 */
const PAYMENT_METHOD_OPTIONS = [
  PaymentMethod.Cheque,
  PaymentMethod.Ach,
  PaymentMethod.Eft,
  PaymentMethod.WireTransfer,
  PaymentMethod.Card,
  PaymentMethod.Cash,
  PaymentMethod.ApplePay,
  PaymentMethod.Other
]

/** Narrows the line-item listing to a single payment method; "All" clears the filter. */
const PaymentMethodFilter = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { dispatch } = useContext(AppContext)
  const selected = searchParams.get(QUERY_PARAMS.RECONCILIATION_PAYMENT_METHOD) || ''

  const handleChange = (event: SelectChangeEvent) => {
    const { value } = event.target
    // Filtering shrinks the result set, so a later page may no longer exist (the API 404s on an out-of-range
    // page). Always drop back to the first page, as the sort control does.
    dispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
    // Read-modify-write against the live URL so this cannot clobber a param another control just set.
    setSearchParams(() => {
      const params = new URLSearchParams(window.location.search)
      if (value) params.set(QUERY_PARAMS.RECONCILIATION_PAYMENT_METHOD, value)
      else params.delete(QUERY_PARAMS.RECONCILIATION_PAYMENT_METHOD)
      return params
    })
  }

  return (
    <FormControl>
      {/* shrink + notched: with displayEmpty the "All" option is an empty value, so MUI would leave the label
          sitting unshrunk on top of the selected text. Pin it to the notch instead. */}
      <InputLabel id="paymentMethodFilter" shrink>
        Payment Method:
      </InputLabel>
      <Select
        labelId="paymentMethodFilter"
        id="paymentMethodFilter"
        label="Payment Method:"
        notched
        displayEmpty
        value={selected}
        onChange={handleChange}
        sx={{ width: 200 }}
        MenuProps={{ anchorOrigin, transformOrigin, style: { maxHeight: 400 } }}
      >
        <MenuItem value="">All</MenuItem>
        {PAYMENT_METHOD_OPTIONS.map(method => (
          <MenuItem key={method} value={method}>
            {getProperPaymentMethod(method)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

export default PaymentMethodFilter
